import React, { useState } from 'react';
import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';

import type { Game } from '../games';
import { CATEGORY_BY_ID } from '../games';
import YieldShooterGame from './YieldShooterGame';
import StudioYieldShooterGame from './StudioYieldShooterGame';
import type { GameResult } from './YieldShooterGame';

type Props = { game: Game; onExit: () => void };

const resultLabel = (r: GameResult): string => {
  switch (r.status) {
    case 'levelPassed':
      return `Level ${r.level} passed`;
    case 'levelFailed':
      return `Level ${r.level} failed`;
    case 'gameCompleted':
      return 'Game completed';
    case 'gameFailed':
      return 'Game over';
    default:
      return r.status;
  }
};

const isGood = (r: GameResult) =>
  r.status === 'levelPassed' || r.status === 'gameCompleted';

/** In-page play surface: the game runs contained inside the Puzzles screen (no
 *  redirect). Bumping `runKey` remounts the game component → clean dispose +
 *  fresh start. */
const GamePlayView: React.FC<Props> = ({ game, onExit }) => {
  const cat = game.category ? CATEGORY_BY_ID[game.category] : undefined;
  const [result, setResult] = useState<GameResult | null>(null);
  const [runKey, setRunKey] = useState(0);

  const restart = () => {
    setResult(null);
    setRunKey(k => k + 1);
  };

  return (
    <div className="puzzles__playview">
      <div className="puzzles__play-bar">
        <button type="button" className="puzzles__back" onClick={onExit}>
          <ArrowLeftOutlined /> All puzzles
        </button>

        <div className="puzzles__play-title">
          <span
            className="puzzles__play-cat"
            style={{ color: cat?.color ?? '#5c6b7f' }}
          >
            {cat?.label ?? 'Game'}
          </span>
          <h2>{game.title}</h2>
        </div>

        <div className="puzzles__play-actions">
          {result && (
            <span
              className={`puzzles__play-result ${isGood(result) ? 'is-good' : 'is-bad'}`}
            >
              {resultLabel(result)} · score {result.score}
            </span>
          )}
          <button type="button" className="puzzles__restart" onClick={restart}>
            <ReloadOutlined /> Restart
          </button>
        </div>
      </div>

      <div className="puzzles__play-stage">
        {game.embed === 'yield-shooter-studio' ? (
          <StudioYieldShooterGame key={runKey} onResult={setResult} onExit={onExit} />
        ) : (
          <YieldShooterGame key={runKey} onResult={setResult} onExit={onExit} />
        )}
      </div>
    </div>
  );
};

export default GamePlayView;
